import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, CircularProgress, Chip, Button,
  Table, TableBody, TableRow, TableCell, TableHead, TableContainer,
  TableSortLabel, alpha, useTheme
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { searchMinistries } from '../services/api';
import useTableSort from '../hooks/useTableSort';

// Raw sort keys for the sortable ministry-list columns.
const MINISTRY_SORT_KEYS = {
  '#': (m) => Number(m.MID) || 0,
  Ministry: (m) => (m.Name || '').toLowerCase(),
  'Sworn In': (m) => m.DateStart || '',
  'End Date': (m) => m.DateEnd || '',
  Duration: (m) => {
    if (!m.DateStart) return 0;
    const end = m.DateEnd ? new Date(m.DateEnd) : new Date();
    return end - new Date(m.DateStart);
  },
};

export default function MinistriesSearch() {
  const theme = useTheme();
  const [ministries, setMinistries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMinistries = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await searchMinistries('*', 1, 100);
        if (data.hits) {
          // Sort by MID descending (most recent first)
          const sorted = [...data.hits].sort((a, b) => b.MID - a.MID);
          setMinistries(sorted);
        }
      } catch (err) {
        console.error('Failed to fetch ministries:', err);
        setError('Failed to load ministries data');
      }
      setLoading(false);
    };
    fetchMinistries();
  }, []);

  // User-sortable headers (#134); default order (MID desc) is unchanged
  // until a header is clicked. Declared before any early return so the
  // Hook order is stable.
  const { order, orderBy, sortedRows, handleSort } = useTableSort(
    ministries,
    (m, col) => (MINISTRY_SORT_KEYS[col] ? MINISTRY_SORT_KEYS[col](m) : ''),
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const isCurrent = (ministry) => {
    return !ministry.DateEnd || new Date(ministry.DateEnd) > new Date();
  };

  const calculateDuration = (start, end) => {
    if (!start) return null;
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : new Date();
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const years = Math.floor(diffDays / 365);
    const months = Math.floor((diffDays % 365) / 30);
    if (years > 0) {
      return months > 0 ? `${years}y ${months}m` : `${years}y`;
    }
    return `${months}m`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">{error}</Typography>
      </Box>
    );
  }

  // Find current ministry
  const currentMinistry = ministries.find(m => isCurrent(m));

  const SortableTh = ({ col, sx }) => (
    <TableCell sx={{ fontWeight: 600, ...sx }} sortDirection={orderBy === col ? order : false}>
      {MINISTRY_SORT_KEYS[col] ? (
        <TableSortLabel
          active={orderBy === col}
          direction={orderBy === col ? order : 'asc'}
          onClick={() => handleSort(col)}
        >
          {col}
        </TableSortLabel>
      ) : col}
    </TableCell>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <AccountBalanceIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4">Government Ministries</Typography>
          <Typography variant="body2" color="text.secondary">
            Australian Federal Government Ministry appointments since Federation (1901)
          </Typography>
        </Box>
      </Box>

      {/* Current Ministry Highlight */}
      {currentMinistry && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.02)} 100%)`,
            border: '1px solid',
            borderColor: alpha(theme.palette.success.main, 0.2)
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip label="Current Ministry" size="small" color="success" />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.dark', mb: 0.5 }}>
                {currentMinistry.Name}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Sworn in {formatDate(currentMinistry.DateStart)}
                {' • '}
                {calculateDuration(currentMinistry.DateStart, null)} in office
              </Typography>
            </Box>
            <Button
              component={RouterLink}
              to={`/ministries/${currentMinistry.MID}`}
              variant="contained"
              color="success"
              endIcon={<ArrowForwardIcon />}
              sx={{ borderRadius: 2 }}
            >
              View Ministry
            </Button>
          </Box>
        </Paper>
      )}

      {/* Ministries List */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Ministry Chronology
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {ministries.length} ministries from Federation to present
          </Typography>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <SortableTh col="#" sx={{ width: '8%' }} />
                <SortableTh col="Ministry" sx={{ width: '35%' }} />
                <SortableTh col="Sworn In" sx={{ width: '18%' }} />
                <SortableTh col="End Date" sx={{ width: '18%' }} />
                <SortableTh col="Duration" sx={{ width: '10%' }} />
                <TableCell sx={{ fontWeight: 600, width: '11%', textAlign: 'right' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((ministry) => {
                const current = isCurrent(ministry);
                return (
                  <TableRow
                    key={ministry.MID}
                    sx={{
                      bgcolor: current ? alpha(theme.palette.success.main, 0.05) : 'inherit',
                      '&:hover': { bgcolor: current ? alpha(theme.palette.success.main, 0.1) : 'action.hover' }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {ministry.MID}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                          component={RouterLink}
                          to={`/ministries/${ministry.MID}`}
                          sx={{
                            p: 0,
                            minWidth: 'auto',
                            textTransform: 'none',
                            fontWeight: 600,
                            color: current ? 'success.dark' : 'primary.main',
                            textAlign: 'left'
                          }}
                        >
                          {ministry.Name}
                        </Button>
                        {current && (
                          <Chip label="Current" size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(ministry.DateStart)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {current ? (
                          <Chip label="In office" size="small" variant="outlined" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                        ) : (
                          formatDate(ministry.DateEnd)
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {calculateDuration(ministry.DateStart, ministry.DateEnd)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>
                      <Button
                        component={RouterLink}
                        to={`/ministries/${ministry.MID}`}
                        size="small"
                        variant="outlined"
                        sx={{ borderRadius: 2 }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
